import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type SidebarPanel = string | null;

interface SetSidebarPanelOptions {
  autoOpen?: boolean;
}

interface LayoutState {
  // Sidebar state
  sidebarOpen: boolean;
  sidebarPanel: SidebarPanel;
  sidebarWidth: number;
  interviewSidebarWidth: number;
  talkToDataSidebarWidth: number;
  talkToDataDetailWidth: number;
  talkToDataCanvasVisible: boolean;
  talkToDataNotesVisible: boolean;

  // Command palette state
  commandPaletteOpen: boolean;

  // Help sheet state
  helpSheetOpen: boolean;
  setHelpSheetOpen: (open: boolean) => void;

  // Actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setSidebarPanel: (panel: SidebarPanel, options?: SetSidebarPanelOptions) => void;
  setSidebarWidth: (width: number) => void;
  setInterviewSidebarWidth: (width: number) => void;
  setTalkToDataSidebarWidth: (width: number) => void;
  setTalkToDataDetailWidth: (width: number) => void;
  setTalkToDataCanvasVisible: (visible: boolean) => void;
  toggleTalkToDataNotesVisible: () => void;
  setTalkToDataNotesVisible: (visible: boolean) => void;
  setCommandPaletteOpen: (open: boolean) => void;
  toggleCommandPalette: () => void;
}

export const useLayoutStore = create<LayoutState>()(
  persist(
    (set) => ({
      // Initial state
      sidebarOpen: true,
      sidebarPanel: null,
      sidebarWidth: 280,
      interviewSidebarWidth: 320,
      talkToDataSidebarWidth: 320,
      talkToDataDetailWidth: 320,
      talkToDataCanvasVisible: true,
      talkToDataNotesVisible: false,
      commandPaletteOpen: false,
      helpSheetOpen: false,

      // Sidebar actions
      toggleSidebar: () => set((state) => ({ 
        sidebarOpen: !state.sidebarOpen 
      })),
      
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      
      setSidebarPanel: (panel, options) => set((state) => ({
        sidebarPanel: panel,
        sidebarOpen: options?.autoOpen === false
          ? state.sidebarOpen
          : panel !== null
            ? true
            : state.sidebarOpen,
      })),
      
      setSidebarWidth: (width) => set({ 
        sidebarWidth: Math.max(200, Math.min(960, width)) // Clamp between 200-960px
      }),

      setInterviewSidebarWidth: (width) => set({
        interviewSidebarWidth: Math.max(200, Math.min(960, width))
      }),

      setTalkToDataSidebarWidth: (width) => set({
        // Kein fester Max-Wert mehr — die effektive Obergrenze ergibt sich aus der
        // Mindestbreite des Source-Panels (Resize-Klemme in TalkToDataPage).
        talkToDataSidebarWidth: Math.max(220, width)
      }),

      setTalkToDataDetailWidth: (width) => set({
        talkToDataDetailWidth: Math.max(220, Math.min(600, width))
      }),

      setTalkToDataCanvasVisible: (visible) => set({ talkToDataCanvasVisible: visible }),

      toggleTalkToDataNotesVisible: () => set((state) => ({
        talkToDataNotesVisible: !state.talkToDataNotesVisible
      })),

      setTalkToDataNotesVisible: (visible) => set({ talkToDataNotesVisible: visible }),

      // Command palette actions
      setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),
      toggleCommandPalette: () => set((state) => ({ 
        commandPaletteOpen: !state.commandPaletteOpen 
      })),

      // Help sheet actions
      setHelpSheetOpen: (open) => set({ helpSheetOpen: open }),
    }),
    {
      name: 'sopho-layout-store',
      version: 1,
      // v1: NotesSidebar startet eingeklappt — einmalig einen evtl. bereits
      // persistierten offenen Zustand auf "geschlossen" zurücksetzen.
      migrate: (persisted) => ({
        ...(persisted as LayoutState),
        talkToDataNotesVisible: false,
      }),
      partialize: (state) => ({
        // Only persist these values (not command palette state)
        sidebarOpen: state.sidebarOpen,
        sidebarPanel: state.sidebarPanel,
        sidebarWidth: state.sidebarWidth,
        interviewSidebarWidth: state.interviewSidebarWidth,
        talkToDataSidebarWidth: state.talkToDataSidebarWidth,
        talkToDataDetailWidth: state.talkToDataDetailWidth,
        talkToDataCanvasVisible: state.talkToDataCanvasVisible,
        talkToDataNotesVisible: state.talkToDataNotesVisible,
      }),
    }
  )
);
